import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, MessageSquare, Coins, CreditCard, Key, Database, CircleDollarSignIcon, } from 'lucide-react';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import AuthContext from '../../AuthContext';
import Header from '../../components/LandingHeader/LandingHeader';
import Loading from '../../components/Loading/Loading';
import BillingView from './BillingView';
import SettingsView from './SettingsView';
import SubscriptionView from './SubscriptionView';
import SupportView from './SupportView';
import WalletView from './WalletView';
import ApiKeysView from './ApiKeysView';
import DbConnectionsView from './DbConnectionsView';
import './Profile.css';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import countryList from 'react-select-country-list';

// GraphQL queries and mutations
const GET_USER_PROFILE = gql`
  query GetUserProfile($sessionId: String!) {
    getUserProfile(sessionId: $sessionId) {
      username
      email
      bio
      phone
      country
      avatarUrl
    }
  }
`;

const UPDATE_USER_PROFILE = gql`
  mutation UpdateUserProfile($input: UpdateProfileInput!) {
    updateUserProfile(input: $input) {
      success
      message
    }
  }
`;

const GET_AVATAR_URL = gql`
  mutation GetAvatarUploadUrl($input: GetAvatarUrlInput!) {
    getAvatarUploadUrl(input: $input) {
      url
      key
      success
      message
    }
  }
`;

const GENERATE_AVATAR_MUTATION = gql`
  mutation GenerateAvatar($prompt: String!, $sessionId: String!) {
    generateAvatar(prompt: $prompt, sessionId: $sessionId) {
      success
      message
      imageUrl
      remainingTries
    }
  }
`;

const UPDATE_AVATAR_MUTATION = gql`
  mutation UpdateAvatar($imageUrl: String!, $sessionId: String!) {
    updateAvatar(imageUrl: $imageUrl, sessionId: $sessionId) {
      success
      message
    }
  }
`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    backgroundColor: '#1f1f1f',
    borderColor: state.isFocused ? '#6B3FC1' : '#374151',
    color: '#fff',
    boxShadow: state.isFocused ? '0 0 0 1px #6B3FC1' : 'none',
    '&:hover': {
      borderColor: '#6B3FC1'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#1f1f1f'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#6B3FC1'
      : state.isFocused
      ? '#27272a'
      : 'transparent',
    color: state.isSelected ? '#fff' : '#e5e7eb',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#6B3FC1'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: '#fff'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af'
  })
};

export default function Profile() {
  const { isAuthenticated, isLoading, sessionId } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  // State initialization
  const [editingField, setEditingField] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState('upload');
  const [activeView, setActiveView] = useState('profile');
  const [uploadError, setUploadError] = useState(null);
  const [avatarPrompt, setAvatarPrompt] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    bio: '',
    phone: '',
    country: ''
  });

  // Country list for react-select
  const countryOptions = useMemo(() => countryList().getData(), []);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // User profile data query
  const {
    error: profileError,
    data,
    refetch,
    loading: profileLoading
  } = useQuery(GET_USER_PROFILE, {
    variables: {
      sessionId: sessionId || ''
    },
    skip: !sessionId,
    fetchPolicy: 'network-only',
    context: {
      headers: {
        'Session-Id': sessionId
      }
    }
  });

  // Profile update mutation
  const [updateProfile, { loading: updateLoading }] = useMutation(UPDATE_USER_PROFILE, {
    context: {
      headers: {
        'Session-Id': sessionId
      }
    }
  });

  // Avatar url mutation
  const [getAvatarUrl, { loading: avatarLoading }] = useMutation(GET_AVATAR_URL, {
    context: {
      headers: {
        'Session-Id': sessionId
      }
    }
  });

  // Generate avatar mutation
  const [generateAvatar, { loading: generateLoading, error: generateError }] = useMutation(
    GENERATE_AVATAR_MUTATION,
    {
      context: {
        headers: {
          'Session-Id': sessionId
        }
      }
    }
  );

  // Update avatar mutation
  const [updateAvatar, { loading: updateAvatarLoading, error: updateAvatarError }] = useMutation(
    UPDATE_AVATAR_MUTATION,
    {
      context: {
        headers: {
          'Session-Id': sessionId
        }
      }
    }
  );

  // Initialize form data from API response
  useEffect(() => {
    if (data?.getUserProfile) {
      const profile = data.getUserProfile;
      setFormData({
        bio: profile.bio || '',
        phone: profile.phone || '',
        country: profile.country || ''
      });

      // Find matching country option
      const foundOption = countryOptions.find(
        (opt) => opt.value === profile.country
      );
      setSelectedCountry(foundOption || null);
    }
  }, [data, countryOptions]);

  // Generate avatar handler
  const handleGenerateAvatar = async () => {
    if (!avatarPrompt.trim()) return;
    
    try {
      setGeneratedImageUrl(null);
      const response = await generateAvatar({
        variables: { prompt: avatarPrompt, sessionId }
      });
      const result = response.data.generateAvatar;
      
      if (result.success) {
        setGeneratedImageUrl(result.imageUrl);
      } else {
        console.error('Error generating avatar:', result.message);
      }
    } catch (err) {
      console.error('Error calling generateAvatar mutation:', err);
    }
  };

  // Save generated avatar handler
  const handleSaveGeneratedImage = async () => {
    if (!generatedImageUrl) return;
    
    try {
      const response = await updateAvatar({
        variables: { sessionId, imageUrl: generatedImageUrl }
      });
      const result = response.data.updateAvatar;
      
      if (result.success) {
        setShowModal(false);
        await refetch();
      } else {
        console.error('Error updating avatar:', result.message);
      }
    } catch (err) {
      console.error('Error in updateAvatar mutation:', err);
    }
  };

  // Handle file drop for avatar upload
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setUploadError(null);

    const file = e.dataTransfer.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Handle file selection for avatar upload
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadError(null);
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // Confirm file upload after preview
  const confirmUpload = async () => {
    if (!selectedFile) return;
    
    try {
      await handleFileUpload(selectedFile);
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError('Failed to upload image. Please try again.');
    }
  };

  // Cancel upload after preview
  const cancelUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
  };

  // Upload file to S3
  const handleFileUpload = async (file) => {
    try {
      setUploadError(null);

      const mutationInput = {
        input: {
          contentType: file.type,
          filename: file.name,
          sessionId
        }
      };

      const response = await getAvatarUrl({
        variables: mutationInput
      });

      if (response.data?.getAvatarUploadUrl?.url) {
        await fetch(response.data.getAvatarUploadUrl.url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        setShowModal(false);
        await refetch();
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.graphQLErrors) {
        console.log('GraphQL Errors:', error.graphQLErrors);
      }
      setUploadError('Failed to upload image. Please try again.');
    }
  };

  // Remove avatar handler
  const handleRemoveAvatar = async () => {
    try {
      const { data: urlData } = await getAvatarUrl({
        variables: {
          input: {
            contentType: 'image/null',
            filename: 'remove',
            sessionId
          }
        }
      });
      
      if (urlData?.getAvatarUploadUrl) {
        await refetch();
      }
    } catch (error) {
      console.error('Error removing avatar:', error);
      setUploadError('Failed to remove avatar. Please try again.');
    }
  };

  // Set field to edit mode
  const handleFieldClick = (fieldName) => {
    setEditingField(fieldName);
  };

  // Handle form field changes
  const handleInputChange = (e, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Handle country selection change
  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setFormData((prev) => ({
      ...prev,
      country: option?.value || ''
    }));
  };

  // Save profile changes
  const handleSaveChanges = async () => {
    try {
      const { data: updateData } = await updateProfile({
        variables: {
          input: {
            ...formData,
            sessionId
          }
        }
      });

      if (updateData?.updateUserProfile?.success) {
        setEditingField(null);
        await refetch();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  // Display loading screen
  if (isLoading || profileLoading) {
    return <Loading />;
  }

  // Display error
  if (profileError) {
    return <div>Error loading profile</div>;
  }

  // Return null if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const userProfile = data?.getUserProfile || {};
  const showSaveButton = editingField !== null;

  return (
    <>
      <Header isProfileView={true} />
      <div className="profile-page">
        <div className="profile-content">
          {/* Sidebar */}
          <div className="profile-sidebar">
          <div className="sidebar-buttons">
  <button
    className={`sidebar-button ${activeView === 'profile' ? 'active' : ''}`}
    onClick={() => setActiveView('profile')}
  >
    <User size={20} />
    <span>{t("profilePage.sidebar.profile")}</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'zenitCoins' ? 'active' : ''}`}
    onClick={() => setActiveView('zenitCoins')}
  >
    <Coins size={20} />
    <span>{t("profilePage.sidebar.billing")}</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'wallet' ? 'active' : ''}`}
    onClick={() => setActiveView('wallet')}
  >
    <CreditCard size={20} />
    <span>Wallet</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'apiKeys' ? 'active' : ''}`}
    onClick={() => setActiveView('apiKeys')}  
  >
    <Key size={20} />
    <span>{t("profilePage.sidebar.apiKeys")}</span>
  </button>

    <button
    className={`sidebar-button ${activeView === 'dbConnections' ? 'active' : ''}`}
    onClick={() => setActiveView('dbConnections')}  
  >
    <Database size={20} />
    <span>{t("profilePage.sidebar.dbConnections")}</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'subscriptions' ? 'active' : ''}`}
    onClick={() => setActiveView('subscriptions')}
  >
    <CircleDollarSignIcon size={20} />
    <span>{t("profilePage.sidebar.subscriptions")}</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'support' ? 'active' : ''}`}
    onClick={() => setActiveView('support')}
  >
    <MessageSquare size={20} />
    <span>{t("profilePage.sidebar.support")}</span>
  </button>

  <button
    className={`sidebar-button ${activeView === 'settings' ? 'active' : ''}`}
    onClick={() => setActiveView('settings')}
  >
    <Settings size={20} />
    <span>{t("profilePage.sidebar.settings")}</span>
  </button>
</div>

          </div>

          {/* Main Content */}
          {activeView === 'profile' ? (
            <>
              <div className="profile-header">
                <h1>{t("profilePage.profileHeaderTitle")}</h1>
                <p>{t("profilePage.profileHeaderSubtitle")}</p>
              </div>

              <div className="profile-main">
                <div className="profile-card">
                  {/* Avatar Section */}
                  <div className="avatar-section">
                    <div className="avatar-container">
                      <div className="avatar-circle">
                        {userProfile.avatarUrl ? (
                          <img
                            src={userProfile.avatarUrl}
                            alt="Profile"
                            className="avatar-image"
                            onError={(e) => {
                              console.error('Error loading image:', e);
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <User size={48} />
                        )}
                      </div>
                    </div>
                    <div className="avatar-buttons">
                      <button
                        className="profile-button"
                        onClick={() => {
                          setShowModal(true);
                          setModalTab('upload');
                          setGeneratedImageUrl(null);
                          setAvatarPrompt('');
                          setSelectedFile(null);
                          setPreviewUrl(null);
                        }}
                        disabled={avatarLoading}
                      >
                        {t("profilePage.avatar.changeBtn")}
                      </button>
                      <button
                        className="profile-button"
                        onClick={handleRemoveAvatar}
                        disabled={avatarLoading}
                      >
                        {t("profilePage.avatar.removeBtn")}
                      </button>
                    </div>
                  </div>

                  <div className="profile-info">
                    {/* BIO */}
                    <div
                      className={`info-section ${editingField === 'bio' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('bio')}
                    >
                      <h3>{t("profilePage.bioLabel")}</h3>
                      {editingField === 'bio' ? (
                        <textarea
                          value={formData.bio}
                          onChange={(e) => handleInputChange(e, 'bio')}
                          className="edit-input"
                          disabled={updateLoading}
                        />
                      ) : (
                        <p>{formData.bio || t("profilePage.addBioPlaceholder")}</p>
                      )}
                    </div>

                    {/* USERNAME */}
                    <div className="info-section">
                      <h3>{t("profilePage.username")}</h3>
                      <p>{userProfile.username}</p>
                    </div>

                    {/* EMAIL */}
                    <div className="info-section">
                      <h3>{t("profilePage.email")}</h3>
                      <p>{userProfile.email}</p>
                    </div>

                    {/* PHONE */}
                    <div
                      className={`info-section ${editingField === 'phone' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('phone')}
                    >
                      <h3>{t("profilePage.phone")}</h3>
                      {editingField === 'phone' ? (
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange(e, 'phone')}
                          className="edit-input"
                          disabled={updateLoading}
                        />
                      ) : (
                        <p>{formData.phone || t("profilePage.addPhonePlaceholder")}</p>
                      )}
                    </div>

                    {/* COUNTRY (react-select) */}
                    <div
                      className={`info-section ${editingField === 'country' ? 'editing' : ''}`}
                      onClick={() => handleFieldClick('country')}
                    >
                      <h3>{t("profilePage.country")}</h3>
                      {editingField === 'country' ? (
                        <Select
                          options={countryOptions}
                          value={selectedCountry}
                          onChange={handleCountryChange}
                          isDisabled={updateLoading}
                          placeholder={t("profilePage.selectCountryPlaceholder")}
                          styles={customSelectStyles}
                        />
                      ) : (
                        <p>{formData.country || t("profilePage.selectCountryPlaceholder")}</p>
                      )}
                    </div>
                  </div>

                  {/* Save changes button */}
                  {showSaveButton && (
                    <div className="save-changes-container">
                      <button
                        className="profile-button save-changes-button"
                        onClick={handleSaveChanges}
                        disabled={updateLoading}
                      >
                        {updateLoading
                          ? t("profilePage.saving")
                          : t("profilePage.saveChanges")
                        }
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : activeView === 'wallet' ? (
            <WalletView availableBalance={userProfile.availableBalance} />
          ) : activeView === 'zenitCoins' ? (
            <BillingView />
          ) : activeView === 'settings' ? (
            <SettingsView />
          ) : activeView === 'subscriptions' ? (
            <SubscriptionView />
          ) : activeView === 'support' ? (
            <SupportView />
          ) : activeView === 'apiKeys' ? (
            <ApiKeysView />
          ) : activeView === 'dbConnections' ? ( 
            <DbConnectionsView />
          ) : (
            <div className="profile-header">
              <h1>{activeView}</h1>
              <p>{t("profilePage.underConstructionMessage")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Avatar change modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-new-design" onClick={(e) => e.stopPropagation()}>
            {/* Modal Title */}
            <h2 className="modal-new-title">
              {modalTab === 'upload'
                ? t("profilePage.modal.uploadPhotoTitle")
                : t("profilePage.modal.generatePhotoTitle")
              }
            </h2>

            {/* Tabs (Upload / Generate) */}
            <div className="modal-new-tabs">
              <div className="modal-new-tab-container">
                <button
                  className={`modal-new-tab-btn ${modalTab === 'upload' ? 'active-tab' : ''}`}
                  onClick={() => {
                    setModalTab('upload');
                    setGeneratedImageUrl(null);
                    setAvatarPrompt('');
                  }}
                >
                  {t("profilePage.modal.uploadTab")}
                </button>
                <button
                  className={`modal-new-tab-btn ${modalTab === 'generate' ? 'active-tab' : ''}`}
                  onClick={() => {
                    setModalTab('generate');
                    setGeneratedImageUrl(null);
                    setAvatarPrompt('');
                  }}
                >
                  {t("profilePage.modal.generateTab")}
                </button>
              </div>
            </div>

            {/* Tab content */}
            <div className="modal-new-content">
              {/* Upload Tab */}
              {modalTab === 'upload' && (
                <div className="modal-upload-section">
                  {!previewUrl ? (
                    <>
                      <div
                        className="modal-upload-dropzone"
                        onDrop={handleDrop}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="upload-cloud-icon">☁</div>
                        <p className="upload-instruction">
                          {t("profilePage.modal.dropImageHere")}
                        </p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                      </div>

                      <button
                        className="upload-select-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={avatarLoading}
                      >
                        {avatarLoading
                          ? t("profilePage.modal.uploading")
                          : t("profilePage.modal.selectFromComputer")
                        }
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="upload-preview-container">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="upload-preview-image"
                        />
                      </div>

                      <div className="generated-buttons">
                        <button
                          className="confirm-generate-btn"
                          onClick={confirmUpload}
                          disabled={avatarLoading}
                        >
                          {avatarLoading
                            ? t("profilePage.modal.saving")
                            : t("profilePage.modal.confirmBtn")
                          }
                        </button>
                        <button
                          className="select-another-btn"
                          onClick={cancelUpload}
                        >
                          {t("profilePage.modal.selectAnotherBtn")}
                        </button>
                      </div>
                    </>
                  )}

                  {/* Upload error message */}
                  {uploadError && (
                    <p className="error-message">{uploadError}</p>
                  )}
                </div>
              )}

              {/* Generate Tab */}
              {modalTab === 'generate' && (
                <div className="modal-generate-section">
                  {!generatedImageUrl ? (
                    <>
                      <label className="generate-label">
                        {t("profilePage.modal.promptLabel")}
                      </label>
                      <textarea
                        className="generate-textarea"
                        placeholder={t("profilePage.modal.placeholderPrompt")}
                        value={avatarPrompt}
                        onChange={(e) => {
                          setAvatarPrompt(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        disabled={generateLoading}
                      />
                      <button
                        className="generate-button"
                        onClick={handleGenerateAvatar}
                        disabled={generateLoading || !avatarPrompt.trim()}
                      >
                        {generateLoading
                          ? t("profilePage.modal.generating")
                          : t("profilePage.modal.generateButton")
                        }
                      </button>
                      {generateError && (
                        <p className="error-message">
                          {generateError.message}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="generated-image-container">
                      <img
                        src={generatedImageUrl}
                        alt="AI Generated"
                        className="generated-avatar"
                      />
                      <div className="generated-buttons">
                        <button
                          className="confirm-generate-btn"
                          onClick={handleSaveGeneratedImage}
                          disabled={updateAvatarLoading}
                        >
                          {updateAvatarLoading
                            ? t("profilePage.modal.saving")
                            : t("profilePage.modal.confirmBtn")
                          }
                        </button>
                        <button
                          className="select-another-btn"
                          onClick={() => {
                            setGeneratedImageUrl(null);
                            setAvatarPrompt('');
                          }}
                        >
                          {t("profilePage.modal.selectAnotherBtn")}
                        </button>
                      </div>
                    </div>
                  )}
                  {updateAvatarError && (
                    <p className="error-message">
                      {updateAvatarError.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Close button */}
            <button className="modal-new-close" onClick={() => setShowModal(false)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}